import React, { useEffect, useMemo, useState } from 'react'

import { AvailabilityIF } from '@driftt/app-config/dist/utils/driftTypes'
import { Paginator, Table } from '@driftt/tide-table'

import { SWRUser } from 'api/report/realTime/types'
import ChartContainer from 'components/core/ChartContainer'
import { renderUserCellWithAvailability } from 'components/core/UserCell'
import {
  EmptyTablePlaceholder,
  FilterTableHeader,
} from 'layouts/RealTimeDashboard/components/TableTooltipHelpers'
import { getFilteredTableData } from 'layouts/RealTimeDashboard/helpers'
import { usePaginatedTableData } from 'layouts/RealTimeDashboard/hooks/usePaginatedTableData'
import { TeamsWithMemberInfo } from 'layouts/RealTimeDashboard/hooks/useTeamsWithMembers'
import { useRTDContext } from 'layouts/RealTimeDashboard/Overview/context'
import { toTideTableRow } from 'utils/component'
import { getSortedTableData, getTableColumns } from 'utils/tableSorting'
import { trackEvent } from 'utils/tracking'

interface AgentStatusBreakdown {
  name: string
  id: number
  availability: AvailabilityIF | undefined
  teamName: string
  teamId: number | undefined
}

export const getAgentStatusBreakdown = (
  teams: TeamsWithMemberInfo,
  allUsers: Record<string, SWRUser>
): Record<number, AgentStatusBreakdown> => {
  const agentStatusBreakdown = {}
  Object.values(teams).forEach((team) => {
    Object.values(team.members).reduce((acc, agent) => {
      acc[agent.id] = {
        name: agent.name,
        id: agent.id,
        availability: agent.availability,
        teamName: team.name,
        teamId: team.id,
      }
      return acc
    }, agentStatusBreakdown)
  })
  const usersWithoutTeam = Object.values(allUsers).filter(
    (agent) => !Object.keys(agentStatusBreakdown).includes(`${agent.id}`)
  )
  usersWithoutTeam.reduce((acc, agent) => {
    acc[agent.id] = {
      name: agent.name,
      id: agent.id,
      availability: agent.availability,
      teamName: '--',
      teamId: undefined,
    }
    return acc
  }, agentStatusBreakdown)
  return agentStatusBreakdown
}

const TeamStatusDrilldown: React.FC = (): JSX.Element => {
  const context = useRTDContext()
  const { teams, isSalesView, filteredAgents, allUsers } = context
  const agentStatusBreakdown = getAgentStatusBreakdown(teams, allUsers)
  const [availabilityFilter, setAvailabilityFilter] = useState<
    Record<string, boolean>
  >({ online: false, offline: false })
  const COLUMNS = useMemo(
    () => [
      {
        trackLabel: 'Teammate',
        label: 'Teammate',
      },
      {
        trackLabel: 'Status',
        label: (
          <FilterTableHeader
            key="status"
            dataOcWebId="RTDTeammateStatusDrilldownAvailabilityFilter"
            text="Status"
            actions={Object.keys(availabilityFilter)}
            activeFilter={availabilityFilter}
            handleFilter={(val) =>
              setAvailabilityFilter({
                ...availabilityFilter,
                [val]: !availabilityFilter[val],
              })
            }
          />
        ),
      },
      {
        trackLabel: 'Team',
        label: 'Team',
      },
    ],
    [availabilityFilter]
  )

  const getTableData = () => {
    const tableData = Object.values(agentStatusBreakdown)
    const dataToShow =
      filteredAgents.length > 0
        ? tableData.filter((agent) => filteredAgents.includes(`${agent.id}`))
        : tableData

    return dataToShow.map((agent) => {
      const agentIsOnline = agent.availability === AvailabilityIF.AVAILABLE

      const data = [
        toTideTableRow(
          renderUserCellWithAvailability({
            ...agent,
            availability: agentIsOnline,
          }),
          { value: agent }
        ),
        toTideTableRow(agentIsOnline ? 'Online' : 'Offline', {
          value: agentIsOnline ? 1 : 0,
        }),
        toTideTableRow(agent.teamName),
      ]

      return {
        data,
      }
    })
  }

  const [sortKey, setSortKey] = useState<number>(1)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const COL_LABELS = COLUMNS.map((c) => c.label)
  const TABLE_COLUMNS = getTableColumns(
    COL_LABELS,
    {},
    {},
    sortKey,
    sortDirection,
    setSortKey,
    setSortDirection
  ).map((c) => {
    c.sortable = false
    c.width = '33%'
    return c
  })

  const tableData = getTableData()

  const filteredTableData = getFilteredTableData(tableData, availabilityFilter)

  const sortedTableData = getSortedTableData(
    filteredTableData,
    sortKey,
    sortDirection
  )

  const { totalPages, onChangePage, paginatedData, pageIndex, PAGE_SIZE } =
    usePaginatedTableData(
      sortedTableData,
      { sortKey, sortDirection, nestedFilter: availabilityFilter },
      7
    )

  useEffect(() => {
    trackEvent('RTDBehavior', {
      event: 'Table Sorting',
      widget: 'RTDTeamStatusDrilldown',
      column: COLUMNS[sortKey].trackLabel,
      view: isSalesView ? 'sales' : 'service',
    })
  }, [COLUMNS, isSalesView, sortKey])

  return (
    <>
      <ChartContainer
        data={tableData}
        renderData={() => (
          <>
            <Table
              isWorking={!teams || !allUsers}
              className={'main-chart'}
              columns={TABLE_COLUMNS}
              data={paginatedData}
            />
            {tableData.length && paginatedData.length === 0 && (
              <EmptyTablePlaceholder text="No data found for selected filters" />
            )}
          </>
        )}
        loading={!teams || !allUsers}
        noDataTitle={'No data for selected teams or teammates'}
      />

      <Paginator
        className="reports-paginator"
        totalPages={totalPages}
        changeSize={() => null}
        index={pageIndex}
        size={PAGE_SIZE}
        goForward={() => onChangePage(1)}
        goBackward={() => onChangePage(-1)}
        sizes={[PAGE_SIZE]}
        position="static"
      />
    </>
  )
}

export default TeamStatusDrilldown
